export type ZoomLevel =
  | "6months"
  | "18months"
  | "1year"
  | "3years"
  | "5years"
  | "10years"
  | "custom";

export const DEFAULT_ZOOM_LEVEL = "18months" as const satisfies Exclude<
  ZoomLevel,
  "custom"
>;

export interface TimelineCustomRange {
  startDate: Date;
  endDate: Date;
}

export interface VisibleDateRangeParams {
  zoomLevel: ZoomLevel;
  zoomCenter?: Date | null;
  startupStartDate: Date;
  startupEndDate?: Date;
  customRange?: TimelineCustomRange | null;
}

/** Add calendar months (handles variable month lengths). */
export function addMonths(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

const ZOOM_MONTHS: Record<Exclude<ZoomLevel, "custom">, number> = {
  "6months": 6,
  "18months": 18,
  "1year": 12,
  "3years": 36,
  "5years": 60,
  "10years": 120,
};

export function getZoomMonthCount(level: ZoomLevel): number | null {
  if (level === "custom") return null;
  return ZOOM_MONTHS[level];
}

export function getZoomDurationMs(
  level: ZoomLevel,
  customRange?: TimelineCustomRange | null,
): number {
  if (level === "custom" && customRange) {
    return (
      customRange.endDate.getTime() - customRange.startDate.getTime()
    );
  }
  const months = ZOOM_MONTHS[level as Exclude<ZoomLevel, "custom">];
  return addMonths(new Date(0), months).getTime();
}

/** Default custom window: ~18 months forward from startup with historical context. */
export function buildDefaultCustomRange(
  startupStartDate: Date,
): TimelineCustomRange {
  const durationMs = getZoomDurationMs("18months");
  const historicalContextMs = durationMs * 0.15;
  return {
    startDate: new Date(startupStartDate.getTime() - historicalContextMs),
    endDate: new Date(
      startupStartDate.getTime() + (durationMs - historicalContextMs),
    ),
  };
}

export function calculateVisibleDateRange({
  zoomLevel,
  zoomCenter,
  startupStartDate,
  startupEndDate,
  customRange,
}: VisibleDateRangeParams): { startDate: Date; endDate: Date } {
  if (zoomLevel === "custom" && customRange) {
    return {
      startDate: new Date(customRange.startDate),
      endDate: new Date(customRange.endDate),
    };
  }

  const effectiveLevel =
    zoomLevel === "custom" ? DEFAULT_ZOOM_LEVEL : zoomLevel;
  const duration = getZoomDurationMs(effectiveLevel);
  const center = zoomCenter || new Date();

  if (zoomCenter) {
    const halfDuration = duration / 2;
    return {
      startDate: new Date(center.getTime() - halfDuration),
      endDate: new Date(center.getTime() + halfDuration),
    };
  }

  const historicalContextDuration = duration * 0.15;
  const startupStart = startupStartDate;
  const startupEnd = startupEndDate;

  let visibleStart: Date;
  let visibleEnd: Date;

  if (startupEnd) {
    const startupDuration = startupEnd.getTime() - startupStart.getTime();

    if (startupDuration < duration - historicalContextDuration) {
      visibleStart = new Date(
        startupStart.getTime() - historicalContextDuration,
      );
      visibleEnd = new Date(
        startupStart.getTime() + (duration - historicalContextDuration),
      );
    } else {
      visibleStart = new Date(
        startupStart.getTime() - historicalContextDuration,
      );
      visibleEnd = new Date(
        startupStart.getTime() + (duration - historicalContextDuration),
      );
    }
  } else {
    visibleStart = new Date(
      startupStart.getTime() - historicalContextDuration,
    );
    visibleEnd = new Date(
      startupStart.getTime() + (duration - historicalContextDuration),
    );
  }

  return { startDate: visibleStart, endDate: visibleEnd };
}

/** Pan offset in months (half the visible window for preset zoom levels). */
export function getPanOffsetMonths(zoomLevel: ZoomLevel): number {
  const months = getZoomMonthCount(zoomLevel);
  if (months === null) return 0;
  return months / 2;
}

export function panDateByZoom(
  date: Date,
  zoomLevel: ZoomLevel,
  direction: "left" | "right",
): Date {
  const sign = direction === "left" ? -1 : 1;
  const effectiveLevel =
    zoomLevel === "custom" ? DEFAULT_ZOOM_LEVEL : zoomLevel;
  const offsetMonths = getPanOffsetMonths(effectiveLevel) * sign;
  return addMonths(date, offsetMonths);
}

export function panCustomRange(
  range: TimelineCustomRange,
  direction: "left" | "right",
): TimelineCustomRange {
  const durationMs =
    range.endDate.getTime() - range.startDate.getTime();
  const offsetMs = (durationMs / 2) * (direction === "left" ? -1 : 1);
  return {
    startDate: new Date(range.startDate.getTime() + offsetMs),
    endDate: new Date(range.endDate.getTime() + offsetMs),
  };
}

export function toDateInputValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseDateInputValue(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
